/**
 * Ausência automática (spec 072, D6) na página Pausas: o limite de
 * inatividade da organização. Mora ao lado dos motivos de pausa porque é a
 * mesma coisa — o status da equipe. O admin escolhe; o resto da equipe vê o
 * valor, para saber quando o próprio status cai.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Label, Select } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/misc';
import { useSetPresenceSettings } from './presence-mutations';
import { idleMinutesLabel, idleMinutesOptions } from './presence-idle';
import { presenceErrorMessage, presenceSettingsQuery } from './presence-queries';

export function IdleSettingsSection({ canEdit }: { canEdit: boolean }) {
  const settingsQ = useQuery(presenceSettingsQuery());
  const save = useSetPresenceSettings();
  const [error, setError] = useState<string | null>(null);
  const current = settingsQ.data?.idleMinutes;

  const change = (value: string) => {
    setError(null);
    save.mutate(
      { idleMinutes: Number(value) },
      { onError: (e) => setError(presenceErrorMessage(e)) }
    );
  };

  return (
    <section className="rounded-lg border shadow-xs">
      <div className="border-b px-4 py-3">
        <h2 className="text-sm font-semibold">Ausência automática</h2>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Quem fica sem atividade no ERP (tecla, clique, mensagem enviada ou ação em ticket) por
          este tempo sai do Disponível e passa a Ausente, a partir da última atividade. A pessoa vê
          um aviso para voltar, e a sessão continua aberta. A pausa não cai sozinha.
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        <Label htmlFor="presence-idle-minutes" className="text-[13px] font-semibold">
          Ficar Ausente depois de
        </Label>
        {settingsQ.isPending ? (
          <Skeleton className="h-8 w-40" />
        ) : settingsQ.isError ? (
          <p className="text-sm text-destructive">{presenceErrorMessage(settingsQ.error)}</p>
        ) : canEdit ? (
          <Select
            id="presence-idle-minutes"
            className="h-8 w-44"
            value={String(current)}
            disabled={save.isPending}
            onChange={(e) => change(e.target.value)}
          >
            {idleMinutesOptions(current).map((m) => (
              <option key={m} value={m}>
                {m === 0 ? 'Desligado' : `${idleMinutesLabel(m)} sem atividade`}
              </option>
            ))}
          </Select>
        ) : (
          <span id="presence-idle-minutes" className="text-sm">
            {current === 0 ? 'Desligado' : `${idleMinutesLabel(current ?? 0)} sem atividade`}
          </span>
        )}
        {save.isSuccess && !save.isPending ? (
          <span className="text-xs text-muted-foreground">Salvo</span>
        ) : null}
      </div>
      {error ? (
        <p className="px-4 pb-3 text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}
    </section>
  );
}
